import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Input, Modal, Select, Spin, Tag, message } from "antd";
import { DeleteOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";

import {
  clearFtcMailbox,
  getFtcAssignments,
  getOfficeContacts,
  revokeFtcAssignment,
  saveFtcAssignment,
  setFtcMailbox,
} from "./controller";

/**
 * Super-admin: who an FTC may correspond with, and from what address.
 *
 * ⚠️ THIS IS A DIFFERENT GRANT FROM THE "OFFICES" BUTTON NEXT DOOR. That one
 * decides whose insurance WORK an FTC sees in their task queue; this decides whose
 * patients' information they may put in an email to a named person. An FTC may
 * legitimately work twenty offices while being the approved correspondent for two,
 * so the two are kept visibly separate — and a task-access change must never
 * silently widen who someone can email.
 *
 * ⚠️ AN OFFICE GRANTED WITH NO CONTACTS PERMITS NOTHING. That is enforced on the
 * server (crm/services/ftc_mail.py) and stated in the UI, because the opposite
 * reading — "none listed means all of them" — would turn a half-finished grant
 * into authorisation for the practice's entire contact list.
 */

export default function FtcCorrespondenceModal({ user, offices, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [mailbox, setMailbox] = useState("");
  const [savingMailbox, setSavingMailbox] = useState(false);

  const [addOffice, setAddOffice] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [saving, setSaving] = useState(false);
  // Which existing grant is being edited (null = adding a new office).
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(
    async function () {
      if (!user) return;
      setLoading(true);
      const res = await getFtcAssignments(user.id);
      const ok = res.status >= 200 && res.status < 300 && Array.isArray(res.json);
      // Never show "no clinics yet" for a failed fetch — an admin would grant
      // access this person may already have.
      setLoadFailed(!ok);
      setAssignments(ok ? res.json.filter((a) => a.is_active) : []);
      setLoading(false);
    },
    [user],
  );

  useEffect(function () {
    if (!user) return;
    setMailbox((user.mailbox && user.mailbox.mailbox_address) || "");
    setAddOffice(null);
    setChosen([]);
    setContacts([]);
    setEditingId(null);
    load();
  }, [user, load]);

  // Contacts are per office, so the picker refills whenever the office changes.
  useEffect(function () {
    if (!addOffice) { setContacts([]); return; }
    let cancelled = false;
    (async function () {
      setContactsLoading(true);
      const res = await getOfficeContacts(addOffice);
      if (cancelled) return;
      setContacts(Array.isArray(res.json) ? res.json : []);
      setContactsLoading(false);
    })();
    return function () { cancelled = true; };
  }, [addOffice]);

  async function saveMailbox() {
    const address = (mailbox || "").trim();
    if (!address) return;
    setSavingMailbox(true);
    try {
      const res = await setFtcMailbox(user.id, address, user.name);
      if (res.status >= 200 && res.status < 300) {
        message.success("Mailbox registered.");
        onChanged();
      } else {
        message.error((res.json && res.json.detail) || "Couldn't register that mailbox.");
      }
    } finally {
      setSavingMailbox(false);
    }
  }

  async function removeMailbox() {
    const res = await clearFtcMailbox(user.id);
    if (res.status >= 200 && res.status < 300) {
      setMailbox("");
      message.success("Mailbox removed. Past correspondence is kept.");
      onChanged();
    }
  }

  async function saveGrant() {
    if (!addOffice) return;
    setSaving(true);
    try {
      const res = await saveFtcAssignment(user.id, addOffice, chosen);
      if (res.status >= 200 && res.status < 300) {
        message.success("Access saved.");
        setAddOffice(null);
        setChosen([]);
        setEditingId(null);
        load();
        onChanged();
      } else {
        message.error((res.json && res.json.detail) || "Couldn't save that access.");
      }
    } finally {
      setSaving(false);
    }
  }

  function editGrant(a) {
    setEditingId(a.id);
    setAddOffice(a.office_id);
    setChosen((a.contacts || []).map((c) => c.id));
  }

  function confirmRevoke(a) {
    Modal.confirm({
      title: `Remove access to ${a.office_name}?`,
      content: `${user.name || user.email} will no longer be able to email anyone at this clinic, and it will disappear from their inbox.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async function () {
        const res = await revokeFtcAssignment(a.id);
        if (res.status >= 200 && res.status < 300) {
          load();
          onChanged();
        } else {
          message.error("Couldn't remove that access.");
        }
      },
    });
  }

  // An office already granted shouldn't be offered again as an addition — edit it.
  const grantedIds = assignments.map((a) => a.office_id);
  const officeOptions = (offices || [])
    .filter((o) => editingId != null || grantedIds.indexOf(o.id) === -1)
    .map((o) => ({ value: o.id, label: o.name }));

  return (
    <Modal
      open={!!user}
      onCancel={onClose}
      title={`Correspondence — ${user ? (user.name || user.email) : ""}`}
      width={680}
      footer={<Button onClick={onClose}>Done</Button>}
      destroyOnClose
    >
      <div className="ftccorr-section">
        <h4>Sends from</h4>
        <p className="ftccorr-hint">
          The shared Office&nbsp;365 mailbox this FTC writes from. There's no sign-in
          step — grant our app access to the mailbox in Azure, then record the
          address here.
        </p>
        <div className="ftccorr-mailbox">
          <Input
            value={mailbox}
            onChange={(e) => setMailbox(e.target.value)}
            placeholder="alice@smilepass.com"
            prefix={<MailOutlined />}
          />
          <Button type="primary" loading={savingMailbox}
                  disabled={!mailbox.trim()} onClick={saveMailbox}>
            Save
          </Button>
          {user && user.mailbox ? (
            <Button danger icon={<DeleteOutlined />} onClick={removeMailbox} />
          ) : null}
        </div>
        {user && !user.mailbox ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message="No mailbox yet — this FTC can't send or receive anything."
          />
        ) : null}
      </div>

      <div className="ftccorr-section">
        <h4>Clinics &amp; approved contacts</h4>
        <p className="ftccorr-hint">
          Separate from the <strong>Offices</strong> button, which controls whose
          work appears in their task queue. This controls who they may email — and a
          clinic granted with <strong>no contacts ticked permits nothing</strong>.
        </p>

        {loading ? (
          <div className="ftccorr-center"><Spin /></div>
        ) : loadFailed ? (
          <Alert
            type="error"
            showIcon
            message="Couldn't load this user's clinics"
            description="The server returned an error, so what's shown below may be incomplete. Reopen this window to retry."
          />
        ) : !assignments.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                 description="No clinics yet — this FTC has nobody to write to." />
        ) : (
          assignments.map((a) => (
            <div className="ftccorr-grant" key={a.id}>
              <div className="ftccorr-grant-head">
                <span className="ftccorr-office">{a.office_name}</span>
                <div>
                  <Button size="small" type="link" onClick={() => editGrant(a)}>Edit</Button>
                  <Button size="small" type="link" danger onClick={() => confirmRevoke(a)}>
                    Remove
                  </Button>
                </div>
              </div>
              {a.contacts && a.contacts.length ? (
                <div className="ftccorr-contacts">
                  {a.contacts.map((c) => (
                    <Tag key={c.id}>{c.name ? `${c.name} · ${c.email}` : c.email}</Tag>
                  ))}
                </div>
              ) : (
                <div className="ftccorr-none">
                  No contacts approved — nothing can be sent to this clinic.
                </div>
              )}
            </div>
          ))
        )}

        <div className="ftccorr-add">
          <div className="ftccorr-add-title">
            {editingId != null ? "Edit access" : "Give access to a clinic"}
          </div>
          <Select
            showSearch
            optionFilterProp="label"
            value={addOffice}
            onChange={(v) => { setAddOffice(v); setChosen([]); }}
            options={officeOptions}
            placeholder="Choose a clinic"
            disabled={editingId != null}
            style={{ width: "100%", marginBottom: 8 }}
          />
          <Select
            mode="multiple"
            value={chosen}
            onChange={setChosen}
            disabled={!addOffice}
            loading={contactsLoading}
            placeholder={
              !addOffice ? "Choose a clinic first"
                : contacts.length ? "Choose the contacts they may email"
                  : "This clinic has no contacts on file yet"
            }
            options={contacts.map((c) => ({
              value: c.id,
              label: c.name ? `${c.name}${c.title ? ` (${c.title})` : ""} · ${c.email}` : c.email,
            }))}
            style={{ width: "100%" }}
          />
          {addOffice && !contacts.length && !contactsLoading ? (
            <p className="ftccorr-hint" style={{ marginTop: 6 }}>
              Add contacts to this clinic first, in its office drawer.
            </p>
          ) : null}
          <div className="ftccorr-add-actions">
            {editingId != null ? (
              <Button onClick={() => { setEditingId(null); setAddOffice(null); setChosen([]); }}>
                Cancel
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={editingId != null ? null : <PlusOutlined />}
              loading={saving}
              disabled={!addOffice}
              onClick={saveGrant}
            >
              {editingId != null ? "Save changes" : "Give access"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
